import Link from "next/link";
import { CALENDLY_URL } from "@/common/site.config";

export function LandingSection({
  children,
  className = "",
  id,
  muted = false,
  narrow = false,
}) {
  return (
    <section
      id={id}
      className={`landing-section ${muted ? "landing-section-muted" : ""} ${className}`}
    >
      <div className={`container ${narrow ? "max-w-4xl" : "max-w-7xl"}`}>
        {children}
      </div>
    </section>
  );
}

export function SectionEyebrow({ children }) {
  // Kept for rare cases (e.g. legal labels). Prefer a strong H2 without a label.
  return (
    <p className="mb-2 text-sm font-medium text-[#1c8c80]">{children}</p>
  );
}

export function SectionTitle({ children, className = "" }) {
  return (
    <h2
      className={`syne-font text-[1.875rem] font-bold leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-[2.875rem] ${className}`}
    >
      {children}
    </h2>
  );
}

export function SectionLead({ children, className = "" }) {
  return (
    <p
      className={`mt-4 max-w-3xl text-[1.0625rem] leading-relaxed text-ink-muted sm:text-lg ${className}`}
    >
      {children}
    </p>
  );
}

export function SectionHeader({
  title,
  lead,
  align = "left",
  className = "",
}) {
  const alignCls =
    align === "center" ? "mx-auto max-w-4xl text-center" : "max-w-4xl text-left";
  return (
    <div className={`${alignCls} ${className}`}>
      <SectionTitle>{title}</SectionTitle>
      {lead ? (
        <SectionLead className={align === "center" ? "mx-auto" : ""}>
          {lead}
        </SectionLead>
      ) : null}
    </div>
  );
}

export function PrimaryButton({
  href,
  children,
  external = false,
  className = "",
}) {
  const cls = `landing-btn-primary ${className}`;
  if (external) {
    return (
      <Link href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <Link href={href} className={cls}>
      {children}
    </Link>
  );
}

export function SecondaryButton({
  href,
  children,
  className = "",
  external = false,
}) {
  const cls = `landing-btn-secondary ${className}`;
  if (external) {
    return (
      <Link href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <Link href={href} className={cls}>
      {children}
    </Link>
  );
}

export function LandingCard({
  children,
  className = "",
  as: Tag = "div",
  href,
}) {
  const cls = `landing-card group transition-all duration-300 hover:-translate-y-0.5 ${className}`;
  if (href) {
    return (
      <Link href={href} className={`${cls} block no-underline`}>
        {children}
      </Link>
    );
  }
  return <Tag className={cls}>{children}</Tag>;
}

export function ContentCard({
  number,
  title,
  description,
  href,
  cta = "Learn more",
  className = "",
}) {
  const inner = (
    <>
      {number ? (
        <p className="text-sm font-semibold text-ink-soft">{number}</p>
      ) : null}
      <h3
        className={`text-xl font-semibold text-ink sm:text-[1.35rem] ${number ? "mt-2" : ""} ${
          href ? "transition-colors group-hover:text-[#1c8c80]" : ""
        }`}
      >
        {title}
      </h3>
      {description ? (
        <p className="mt-3 text-[0.9875rem] leading-relaxed text-ink-muted sm:text-base">
          {description}
        </p>
      ) : null}
      {href ? (
        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-ink">
          {cta}
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
            <path
              d="M8.55747 2.94434L1.16382 10.2695L0 9.10572L7.32519 1.71206H0.889977V0.00056839H10.269V9.37955H8.55747V2.94434Z"
              fill="currentColor"
            />
          </svg>
        </span>
      ) : null}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={`landing-card group block h-full no-underline transition-all duration-300 hover:-translate-y-0.5 ${className}`}
      >
        {inner}
      </Link>
    );
  }

  return <div className={`landing-card ${className}`}>{inner}</div>;
}

export function IconWell({ children, className = "" }) {
  return (
    <div
      className={`grid h-14 w-14 shrink-0 place-items-center rounded-xl border border-line bg-muted text-[#1c8c80] ${className}`}
    >
      {children}
    </div>
  );
}

export function PageHero({
  title,
  titleAccent,
  lead,
  secondaryLead,
  primaryCta,
  secondaryCta,
  children,
  compact = false,
  wide = false,
  align = "left",
}) {
  const alignCls =
    align === "center"
      ? "mx-auto items-center text-center"
      : "items-start text-left";
  const widthCls = wide ? "max-w-6xl" : "max-w-4xl";
  const padCls = compact ? "py-10 md:py-12" : "py-14 md:py-16 lg:py-20";

  return (
    <section className="relative overflow-hidden">
      <div className="landing-hero-glow" aria-hidden />
      <div className="landing-hero-grid" aria-hidden />
      <div className={`container relative z-10 max-w-7xl ${padCls}`}>
        <div
          className={`page-hero-animate flex flex-col ${alignCls} ${widthCls}`}
        >
          <h1 className="syne-font text-3xl font-bold leading-[1.15] tracking-tight text-ink sm:text-4xl md:text-5xl lg:text-[3.5rem]">
            {title}
            {titleAccent ? (
              <>
                {" "}
                <span className="text-main">{titleAccent}</span>
              </>
            ) : null}
          </h1>
          {lead ? (
            <p
              className={`mt-5 text-[1.0625rem] leading-relaxed text-ink-muted md:text-lg ${
                align === "center" ? "max-w-3xl" : "max-w-4xl"
              }`}
            >
              {lead}
            </p>
          ) : null}
          {secondaryLead ? (
            <p
              className={`mt-3 text-[1.0625rem] leading-relaxed text-ink-muted ${
                align === "center" ? "max-w-3xl" : "max-w-4xl"
              }`}
            >
              {secondaryLead}
            </p>
          ) : null}
          {(primaryCta || secondaryCta) && (
            <div
              className={`mt-8 flex flex-wrap items-center gap-3 ${
                align === "center" ? "justify-center" : ""
              }`}
            >
              {primaryCta}
              {secondaryCta}
            </div>
          )}
          {children}
        </div>
      </div>
    </section>
  );
}

export function StatRow({ items = [] }) {
  return (
    <div className="mx-auto mt-14 flex max-w-4xl flex-col items-center justify-center gap-6 border-t border-line pt-8 sm:flex-row sm:gap-0">
      {items.map((stat) => (
        <div key={stat.value} className="landing-stat text-center sm:text-left">
          <p className="text-sm font-semibold text-ink">{stat.value}</p>
          <p className="mt-0.5 text-xs text-ink-soft">{stat.label}</p>
        </div>
      ))}
    </div>
  );
}

export function ProcessRail({ steps = [] }) {
  return (
    <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {steps.map((step, index) => (
        <div
          key={step.title}
          className="process-step rounded-2xl border border-line bg-gradient-to-b from-surface to-muted p-6"
        >
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg border border-line bg-surface text-sm font-bold text-ink">
            {String(index + 1).padStart(2, "0")}
          </div>
          <h3 className="text-base font-semibold text-ink">{step.title}</h3>
          <p className="mt-2 text-[0.9875rem] leading-relaxed text-ink-muted">
            {step.description}
          </p>
        </div>
      ))}
    </div>
  );
}

export function FaqList({ items = [] }) {
  return (
    <div className="mt-10 max-w-4xl space-y-4">
      {items.map((item) => (
        <details
          key={item.question}
          className="group rounded-2xl border border-line bg-surface px-5 py-4 open:border-[#5aada4]/45"
        >
          <summary className="cursor-pointer list-none text-base font-semibold text-ink marker:content-none [&::-webkit-details-marker]:hidden">
            <span className="flex items-center justify-between gap-4">
              {item.question}
              <span className="text-[#1c8c80] transition-transform group-open:rotate-45">
                +
              </span>
            </span>
          </summary>
          <p className="mt-3 text-sm leading-relaxed text-ink-muted">
            {item.answer}
          </p>
        </details>
      ))}
    </div>
  );
}

export function CtaBand({
  title,
  description,
  primaryText = "Talk to an architect",
  primaryHref = CALENDLY_URL,
  secondaryText,
  secondaryHref = "/contact",
}) {
  const isExternal = /^https?:\/\//i.test(primaryHref);

  return (
    <section className="theme-band-dark relative overflow-hidden py-16 md:py-20">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(ellipse 70% 80% at 20% 50%, rgba(28,140,128,0.35), transparent), radial-gradient(ellipse 50% 60% at 90% 20%, rgba(90,173,164,0.2), transparent)",
        }}
        aria-hidden
      />
      <div className="container relative z-10 max-w-7xl">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="syne-font text-2xl font-bold tracking-tight text-white md:text-3xl lg:text-[2.75rem]">
            {title}
          </h2>
          {description ? (
            <p className="mt-4 text-base leading-relaxed text-slate-300">
              {description}
            </p>
          ) : null}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              href={primaryHref}
              {...(isExternal
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              className="landing-btn-primary"
            >
              {primaryText}
            </Link>
            {secondaryText ? (
              <Link
                href={secondaryHref}
                className="inline-flex items-center justify-center rounded-full border border-white/25 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white no-underline transition-colors hover:border-[#5aada4] hover:text-[#5aada4]"
              >
                {secondaryText}
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export function AudienceSplit({ forItems = [], notForItems = [] }) {
  return (
    <div className="mt-12 grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-line bg-surface p-7">
        <h3 className="text-lg font-semibold text-ink">A strong fit when</h3>
        <ul className="mt-5 space-y-3">
          {forItems.map((item) => (
            <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink-muted">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1c8c80]" />
              {item}
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl border border-line bg-muted/60 p-7">
        <h3 className="text-lg font-semibold text-ink">Not the right fit when</h3>
        <ul className="mt-5 space-y-3">
          {notForItems.map((item) => (
            <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink-muted">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ink-soft" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
